// Document Upload Store

import { create } from "zustand";
import { StringsKeys } from "@/core/internationalization/strings";
import Exception from "@/core/exceptions/Exception";
import DefaultExceptionTypes from "@/core/exceptions/DefaultExceptionTypes";
import ExceptionService from "@/core/exceptions/exceptionService/ExceptionService";
import DocumentUploadRepository from "@/core/customers/repositories/DocumentUploadRepository";
import UploadDocumentUseCase from "@/core/customers/useCases/UploadDocumentUseCase/UploadDocumentUseCase";
import GetDocumentsUseCase from "@/core/customers/useCases/GetDocumentsUseCase/GetDocumentsUseCase";
import DeleteDocumentUseCase from "@/core/customers/useCases/DeleteDocumentUseCase/DeleteDocumentUseCase";
import GetRequiredDocumentsUseCase from "@/core/customers/useCases/GetRequiredDocumentsUseCase/GetRequiredDocumentsUseCase";
import ToastService from "@/app/shared/services/toastService";
import {
  DocumentUploadRequest,
  DocumentUploadResponse,
  DocumentsListResponse,
  DeleteDocumentRequest,
  DeleteDocumentResponse,
  Document,
} from "@/core/customers/entities/DocumentUpload";
import { RequiredDocument } from "@/core/customers/entities/RequiredDocuments";
import { ServiceContainer } from "@/core/di";

interface DocumentUploadState {
  // Upload state
  uploading: boolean;
  uploadError: string | null;
  uploadSuccess: boolean;

  // Documents list state
  documents: Document[];
  documentsLoading: boolean;
  documentsError: string | null;

  // Delete state
  deleting: boolean;
  deleteError: string | null;
  deleteSuccess: boolean;


  // Required documents state
  requiredDocuments: RequiredDocument[];
  requiredDocumentsLoading: boolean;
  requiredDocumentsError: string | null;

  // Exceptions
  failedUploadException: Exception | null;
  failedDocumentsException: Exception | null;
  failedDeleteException: Exception | null;

  // Actions
  setUploading: (uploading: boolean) => void;
  setUploadError: (error: string | null) => void;
  setUploadSuccess: (success: boolean) => void;
  setDocuments: (documents: Document[]) => void;
  setDocumentsLoading: (loading: boolean) => void;
  setDocumentsError: (error: string | null) => void;
  setDeleting: (deleting: boolean) => void;
  setDeleteError: (error: string | null) => void;
  setDeleteSuccess: (success: boolean) => void;
  setRequiredDocuments: (documents: RequiredDocument[]) => void;
  setRequiredDocumentsLoading: (loading: boolean) => void;
  setRequiredDocumentsError: (error: string | null) => void;
  resetState: () => void;

  // Async actions
  uploadDocument: (
    request: DocumentUploadRequest,
    translate: (key: StringsKeys) => string
  ) => Promise<{
    success: boolean;
    data?: DocumentUploadResponse["data"];
    error?: string;
  }>;

  fetchDocuments: (
    customerId: string,
    translate: (key: StringsKeys) => string
  ) => Promise<DocumentsListResponse>;

  deleteDocument: (
    request: DeleteDocumentRequest,
    translate: (key: StringsKeys) => string
  ) => Promise<{
    success: boolean;
    data?: DeleteDocumentResponse;
    error?: string;
  }>;

  fetchRequiredDocuments: (
    businessKey: string,
    translate: (key: StringsKeys) => string
  ) => Promise<{
    success: boolean;
    data?: RequiredDocument[];
    error?: string;
  }>;
}

// Repositories resolved at module scope via the DI container (typed keys).
const sc = ServiceContainer.getInstance();
let documentUploadRepository: DocumentUploadRepository =
  sc.getRepository("documentUpload");
const requiredDocumentsRepository = sc.getRepository("requiredDocuments");

// Kept for DocumentUploadServiceProvider: allows swapping the repository
// (e.g. mock vs backend) without touching the store.
export const setDocumentUploadRepository = (
  repository: DocumentUploadRepository
) => {
  documentUploadRepository = repository;
};

export const useDocumentUploadStore = create<DocumentUploadState>(
  (set, get) => ({
    // Initial state
    uploading: false,
    uploadError: null,
    uploadSuccess: false,
    documents: [],
    documentsLoading: false,
    documentsError: null,
    deleting: false,
    deleteError: null,
    deleteSuccess: false,
    requiredDocuments: [],
    requiredDocumentsLoading: false,
    requiredDocumentsError: null,
    failedUploadException: null,
    failedDocumentsException: null,
    failedDeleteException: null,

    // State setters
    setUploading: (uploading: boolean) => set({ uploading }),
    setUploadError: (error: string | null) => set({ uploadError: error }),
    setUploadSuccess: (success: boolean) => set({ uploadSuccess: success }),
    setDocuments: (documents: Document[]) => set({ documents }),
    setDocumentsLoading: (loading: boolean) =>
      set({ documentsLoading: loading }),
    setDocumentsError: (error: string | null) =>
      set({ documentsError: error }),
    setDeleting: (deleting: boolean) => set({ deleting }),
    setDeleteError: (error: string | null) => set({ deleteError: error }),
    setDeleteSuccess: (success: boolean) => set({ deleteSuccess: success }),
    setRequiredDocuments: (documents: RequiredDocument[]) =>
      set({ requiredDocuments: documents }),
    setRequiredDocumentsLoading: (loading: boolean) =>
      set({ requiredDocumentsLoading: loading }),
    setRequiredDocumentsError: (error: string | null) =>
      set({ requiredDocumentsError: error }),
    resetState: () =>
      set({
        uploading: false,
        uploadError: null,
        uploadSuccess: false,
        documents: [],
        documentsLoading: false,
        documentsError: null,
        deleting: false,
        deleteError: null,
        deleteSuccess: false,
                    requiredDocuments: [],
        requiredDocumentsLoading: false,
        requiredDocumentsError: null,
        failedUploadException: null,
        failedDocumentsException: null,
        failedDeleteException: null,
      }),

    // Async actions
    uploadDocument: async (
      request: DocumentUploadRequest,
      translate: (key: StringsKeys) => string
    ) => {
      // Show loading toast like createAccount store
      const loadingToast = ToastService.loading("Upload du document...", {
        description: "Veuillez patienter pendant le téléversement du document.",
      });

      set({ uploading: true, uploadError: null, uploadSuccess: false });

      let result: {
        success: boolean;
        data?: DocumentUploadResponse["data"];
        error?: string;
      };
      try {
        const response: DocumentUploadResponse = await new UploadDocumentUseCase(
          documentUploadRepository
        ).execute(request);

        if (response.success) {
          set({ uploadSuccess: true });

          // Add the new document to the list, mapping API response to Document entity
          if (response.data) {
            const newDocument: Document = {
              id: response.data.id,
              name: response.data.documentName || "",
              type: response.data.documentType || "",
              url: "",
              createdAt: new Date().toISOString(),
              size: response.data.size,
              mimeType: response.data.contentType,
            };
            set({ documents: [...get().documents, newDocument] });
          }

          // Return the document data directly, not the wrapped DocumentUploadResponse
          result = { success: true, data: response.data };
        } else {
          const errorMessage = response.message || "Failed to upload document";
          set({ uploadError: errorMessage });
          result = { success: false, error: errorMessage };
        }
      } catch (e: unknown) {
        const exception = ExceptionService.handleException<
          undefined,
          DefaultExceptionTypes
        >(e as Exception<undefined, DefaultExceptionTypes>, translate);

        const errorMessage =
          exception.message || translate(StringsKeys.unexpectedError);
        set({ uploadError: errorMessage, failedUploadException: exception });
        result = { success: false, error: errorMessage };
      } finally {
        set({ uploading: false });
      }

      if (result.success) {
        ToastService.success("Document uploadé avec succès !", {
          id: loadingToast,
        });

        // Clear success flag to avoid replay on remount
        set({ uploadSuccess: false });
      } else {
        const errorMessage =
          result.error || "Une erreur est survenue lors de l'upload.";
        ToastService.error("Erreur lors de l'upload du document", {
          description: errorMessage,
          id: loadingToast,
        });

        // Clear error so it doesn't toast again on remount
        set({ uploadError: null });
      }

      return result;
    },

    fetchDocuments: async (
      customerId: string,
      translate: (key: StringsKeys) => string
    ) => {
      set({
        failedDocumentsException: null,
        documentsLoading: true,
        documentsError: null,
      });

      try {
        const documentsResponse = await new GetDocumentsUseCase(
          documentUploadRepository
        ).execute(customerId);

        if (documentsResponse.success) {
          set({ documents: documentsResponse.data });
        } else {
          set({ documentsError: documentsResponse.message });
        }

        return documentsResponse;
      } catch (e) {
        const failedDocumentsException = ExceptionService.handleException<
          undefined,
          DefaultExceptionTypes
        >(
          e as Exception<undefined, DefaultExceptionTypes>,
          translate,
          (exceptionType: DefaultExceptionTypes) => {
            if (exceptionType === DefaultExceptionTypes.NETWORK_ERROR) {
              return translate(StringsKeys.networkError);
            }
            return null;
          }
        );

        set({
          documentsError: failedDocumentsException.message ?? null,
          failedDocumentsException,
        });

        throw failedDocumentsException;
      } finally {
        set({ documentsLoading: false });
      }
    },

    deleteDocument: async (
      request: DeleteDocumentRequest,
      translate: (key: StringsKeys) => string
    ) => {
      set({ deleting: true, deleteError: null, deleteSuccess: false });

      try {
        const result = await new DeleteDocumentUseCase(
          documentUploadRepository
        ).execute(request);

        if (result.success) {
          // Remove the document from the list
          set({
            deleteSuccess: true,
            documents: get().documents.filter(
              (doc) => doc.id !== request.documentId
            ),
          });

          return { success: true, data: result };
        } else {
          const errorMessage = result.message || "Failed to delete document";
          set({ deleteError: errorMessage });
          return { success: false, error: errorMessage };
        }
      } catch (e: unknown) {
        const exception = ExceptionService.handleException<
          undefined,
          DefaultExceptionTypes
        >(e as Exception<undefined, DefaultExceptionTypes>, translate);

        const errorMessage =
          exception.message || translate(StringsKeys.unexpectedError);
        set({ deleteError: errorMessage, failedDeleteException: exception });

        return { success: false, error: errorMessage };
      } finally {
        set({ deleting: false });
      }
    },


    fetchRequiredDocuments: async (
      businessKey: string,
      translate: (key: StringsKeys) => string
    ) => {
      void translate;
      set({
        requiredDocumentsLoading: true,
        requiredDocumentsError: null,
      });

      try {
        const response = await new GetRequiredDocumentsUseCase(
          requiredDocumentsRepository
        ).execute(businessKey);

        if (response.success && response.data) {
          set({
            requiredDocuments: response.data,
            requiredDocumentsLoading: false,
            requiredDocumentsError: null,
          });

          return {
            success: true,
            data: response.data,
          };
        } else {
          const errorMessage =
            response.message ||
            "Erreur lors de la récupération des documents requis";
          set({
            requiredDocuments: [],
            requiredDocumentsLoading: false,
            requiredDocumentsError: errorMessage,
          });

          return {
            success: false,
            error: errorMessage,
          };
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Erreur réseau lors de la récupération des documents requis";

        set({
          requiredDocuments: [],
          requiredDocumentsLoading: false,
          requiredDocumentsError: errorMessage,
        });

        return {
          success: false,
          error: errorMessage,
        };
      }
    },
  })
);
